import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { glossary } from "../lib/content";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import LastReviewed from "../components/LastReviewed";
import { Search } from "lucide-react";

export default function GlossaryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");

  useEffect(() => {
    // Keep URL and input in sync when the URL changes (deep-linked search from header)
    const urlQ = searchParams.get("q") || "";
    if (urlQ !== q) setQ(urlQ);
  }, [searchParams]);

  const onSearchChange = (v) => {
    setQ(v);
    const next = new URLSearchParams(searchParams);
    if (v) next.set("q", v);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return glossary;
    return glossary.filter(
      (g) =>
        g.term.toLowerCase().includes(s) ||
        g.definition.toLowerCase().includes(s)
    );
  }, [q, glossary]);

  return (
    <div data-testid="glossary-page">
      <PageMeta
        title="Glossary"
        description="Section 47, ICO, EPO, LADO — the child protection system speaks in initials. Here is what they actually mean, in plain English."
        path="/glossary"
      />
      <PageHeader
        overline="A small dictionary"
        title="Glossary of terms."
        lede="The system speaks in initials. Here is what they actually mean — written in the kind of English you can read out loud."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20 md:py-24">
        <div className="max-w-2xl mb-12">
          <label htmlFor="glossary-search" className="sr-only">
            Search glossary
          </label>
          <div className="flex items-center gap-3 border border-rule bg-white px-4 py-3">
            <Search size={18} strokeWidth={1.5} className="text-ink-muted" />
            <input
              id="glossary-search"
              data-testid="glossary-search-input"
              type="text"
              value={q}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search a term, e.g. section 20"
              className="flex-1 bg-transparent text-base outline-none placeholder:text-ink-muted/60"
            />
          </div>
        </div>

        <div
          data-testid="glossary-list"
          className="divide-y divide-rule border-y border-rule"
        >
          {filtered.length === 0 && (
            <div className="py-10 text-ink-muted">
              No terms match &ldquo;{q}&rdquo;. Try a different word.
            </div>
          )}
          {filtered.map((g) => (
            <div
              key={g.term}
              data-testid={`glossary-item-${g.term.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-10 py-7"
            >
              <div className="md:col-span-4">
                <div className="font-serif text-2xl text-ink tracking-tight">
                  {g.term}
                </div>
              </div>
              <div className="md:col-span-8 text-ink-muted leading-relaxed text-base md:text-lg font-light">
                {g.definition}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <LastReviewed path="/glossary" />
        </div>
      </section>
    </div>
  );
}
